import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  ScrollAreaAutosize,
  Text,
} from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import { MAX_FILENAME_LENGTH_BEFORE_UPLOAD, MEGABYTE } from "@constants/index";

import { useUpdateLabGroup } from "@hooks/mutations/lab/useUpdateLabGroup";

import { Laboratory } from "@customTypes/laboratory";

import { StyledEditModalContent } from "../EditModal/EditModal.styled";


interface LabGroupModalProps {
  lab: Partial<Laboratory>;
  opened: boolean;
  close: () => void;
}

export const LabGroupModal = ({ lab, opened, close }: LabGroupModalProps) => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);

  const { mutate: updateLabGroup, isLoading: isUpdateLabGroupLoading } =
    useUpdateLabGroup();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdateLabGroup = () => {
    const jsFormData = new FormData();
    uploadedFiles.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    updateLabGroup(
      {
        lab: jsFormData,
        labGroupId: lab?.labGroupId as string,
        patientId: lab?.patientId as string,
      },
      { onSuccess: () => close() }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={<Text fw={500}>{t("edit-lab-group")}</Text>}>
      <StyledEditModalContent>
        <div className="files-wrapper">
          <div
            {...getRootProps({
              className: `dropzone ${isDragActive ? "active" : ""} ${
                isDragAccept ? "accept" : ""
              } ${isDragReject ? "reject" : ""}`,
            })}>
            <input {...getInputProps()} />

            <div className="text-content">
              <div className="svg-wrapper">
                <LiaFileUploadSolid />
              </div>
              <div className="p-wrapper">
                <p>{t("drag-file-here")}</p>
                <p>{t("drag-file-here-continuation")}</p>
              </div>
            </div>
          </div>
          <div className="uploaded-files">
            <h4>{t("uploaded-files")}</h4>
            {!uploadedFiles.length ? (
              <Alert
                mt={"md"}
                variant="light"
                color="blue"
                icon={<IconInfoCircle />}>
                <Text fz={14} lh={1.5} c="blue">
                  {t("files-appear-info")}
                </Text>
              </Alert>
            ) : (
              <ScrollAreaAutosize h={200} type="auto" mt="sm">
                <ul>
                  {uploadedFiles.map(file => (
                    <li key={file.name}>
                      <div className="content">
                        {file.type === "application/pdf" ? (
                          <AiOutlineFilePdf />
                        ) : (
                          <AiOutlineFileImage />
                        )}
                        <p>
                          {truncateWithEllipsis(
                            file.name,
                            MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                          )}{" "}
                          ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                        </p>
                      </div>

                      <ActionIcon
                        color="red"
                        variant="outline"
                        onClick={() => removeFile(file)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </li>
                  ))}
                </ul>
              </ScrollAreaAutosize>
            )}
          </div>
        </div>

        <Group justify="end" mt="xl">
          <PermissionGate permission="labs.edit"><Button
            onClick={handleUpdateLabGroup}
            loading={isUpdateLabGroupLoading}>
            {t("update-lab-group")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
